import { useEffect, useState } from 'react';
import axios from 'axios';

import errorReporter from '@app/errorReporter';
import { useDebounce } from 'hooks';
import { AppointmentController } from 'api';
import type {
  GetPermissionsAppointmentResponse,
  GetTasksForUserResponseBody,
} from '@/app-package';

type TaskItemsType = GetTasksForUserResponseBody['tasks'];

const useTasks = (requestType: 'user' | 'team', targetTeamId?: string) => {
  const [totalCount, setTotalCount] = useState(0);
  const [taskItems, setTaskItems] = useState<TaskItemsType>([]);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [permissionsMap, setPermissionsMap] = useState<
    Record<string, GetPermissionsAppointmentResponse>
  >({});

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const fetchTasks = async () => {
    setIsTasksLoading(true);
    try {
      const {
        data: { tasks, count },
      } = await axios.get<GetTasksForUserResponseBody>(
        `/${requestType}/tasks/0`,
        {
          params: {
            text: debouncedSearchValue,
            includeDocument: requestType === 'user',
            targetTeamId,
            ...(requestType === 'user'
              ? {
                  taskTypes: [
                    'offer801Verification',
                    'offerNotification',
                    'appointment',
                  ],
                }
              : {}),
          },
        },
      );
      const appointmentIds = tasks
        .map(appointmentTask => {
          if (appointmentTask.taskType === 'appointment') {
            return appointmentTask.data.appointmentId;
          }
          return null;
        })
        .filter(Boolean);
      if (appointmentIds.length) {
        const fetchedPermissionMap =
          await AppointmentController.getPermissionsManyAppointments(
            appointmentIds,
          );
        setPermissionsMap(fetchedPermissionMap);
      }
      setTaskItems(tasks);
      setTotalCount(count);
    } catch (err) {
      errorReporter.error(err);
    } finally {
      setIsTasksLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [requestType, debouncedSearchValue, targetTeamId]);

  return {
    totalCount,
    taskItems,
    isTasksLoading,
    taskSearchValue: searchValue,
    setTaskSearchValue: setSearchValue,
    permissionsMap,
    refetchTasks: fetchTasks,
  };
};

export default useTasks;
